import type { Character } from '../domain/character.ts';
import type { StatId } from '../domain/stats.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import type { AttackProfile, Skill } from '../content/types.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import { resolveAttackCheck } from '../rules/attackResolution.ts';
import type { AttackCheckOutcome } from '../rules/attackResolution.ts';
import { rollDamage } from '../rules/damageRoll.ts';
import type { DamageRollResult } from '../rules/damageRoll.ts';
import { spendCharacterMana } from './healthService.ts';
import type { EffectiveCharacter } from './effectiveCharacter.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import type { CheckContext } from '../rules/effects.ts';
import type { CooldownScope } from '../domain/encounter.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { isOnCooldown, startCooldown } from '../rules/encounter.ts';

/**
 * Composition layer for attacks. Holds no rules.
 *
 * The two halves stay apart on purpose:
 *   attackAgainst()  - resolves the Check against a target number;
 *   damageFor()      - rolls damage for an already-resolved attack.
 *
 * A future GM module will supply the target number and then apply the damage to
 * a Mob, without either function changing.
 */

export interface AttackPlan {
  skill: Skill;
  profile: AttackProfile;
  toHitStat: StatId;
  toHitMod: number;
  rank: number;
  /** "d20 + Dex Mod (+2) + Dagger Rank (+3)" */
  toHitFormula: string;
  /** "1d4 + Str Mod (+1) Piercing" */
  damageFormula: string;
  manaCost: number | null;
  notes: string[];
}

/**
 * Convenience for callers holding a plain Character and NO catalogue.
 *
 * Builds the crawler from its own Stats and Skills alone: no Buffs, no Debuffs
 * and NO WORN GEAR. That is right for a rules test asking what a Skill does in
 * isolation, and wrong for anything the player is looking at. The application
 * uses the `...For` variants with an EffectiveCharacter built by the caller.
 */
export function planAttack(
  character: Character,
  skill: Skill,
  registry: ContentRegistry,
  overrideToHitStat?: StatId,
): AttackPlan | null {
  return planAttackFor(buildEffectiveCharacter(character), skill, registry, overrideToHitStat);
}

export function attacksFor(character: Character, registry: ContentRegistry): Skill[] {
  return character.skills
    .map((s) => registry.getSkill(s.skillId))
    .filter((s): s is Skill => s !== undefined && registry.getAttack(s.id) !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function planAttackFor(
  effective: EffectiveCharacter,
  skill: Skill,
  registry: ContentRegistry,
  overrideToHitStat?: StatId,
): AttackPlan | null {
  const profile = registry.getAttack(skill.id);
  if (!profile) return null;

  // Same effective Stats and Ranks the Roll Tray uses. No attack-specific
  // Enhanced Stat implementation exists anywhere.
  const rank = effective.effectiveSkillRank(skill.id);
  const toHitStat = overrideToHitStat ?? profile.toHitStat;
  const toHitMod = effective.statMod(toHitStat);
  const damageStat = profile.baseDamage.stat;
  const damageMod = damageStat ? effective.statMod(damageStat) : null;

  const dice =
    rank >= 5 && profile.rank5.addedDamageDice
      ? `${profile.baseDamage.dice} + ${profile.rank5.addedDamageDice} (Rank 5)`
      : profile.baseDamage.dice;

  const notes: string[] = [...profile.limitations];
  if (profile.cooldown) notes.push(`Cooldown: ${profile.cooldown}`);
  if (profile.aiFavor) {
    notes.push(
      `AI Favor ${profile.aiFavor.amount}${profile.aiFavor.condition ? ` (${profile.aiFavor.condition})` : ''}`,
    );
  }
  if (profile.range?.feet) notes.push(`Range ${profile.range.feet} ft`);
  if (profile.range?.kind === 'melee') notes.push('Range: Melee (5 ft)');
  if (profile.area) notes.push(`${profile.area.feet} ft ${profile.area.kind} radius`);
  if (rank >= 5) notes.push(...profile.rank5.notes);

  return {
    skill,
    profile,
    toHitStat,
    toHitMod,
    rank,
    toHitFormula: `d20 + ${STAT_LABELS[toHitStat].abbr} Mod (${signed(toHitMod)})${
      rank > 0 ? ` + ${skill.name} Rank (${signed(rank)})` : ' (untrained: Disadvantage, no Ranks)'
    }`,
    damageFormula: `${dice}${
      damageStat && damageMod !== null ? ` + ${STAT_LABELS[damageStat].abbr} Mod (${signed(damageMod)})` : ''
    } ${profile.baseDamage.type}`,
    manaCost: profile.manaCost,
    notes,
  };
}

/** Step one: does it land? `targetEvade` comes from the GM, or is null. */
export function attackAgainstFor(
  roller: Roller,
  effective: EffectiveCharacter,
  plan: AttackPlan,
  mode: RollMode,
  targetEvade: number | null,
  extraComponents: readonly { label: string; value: number }[] = [],
): AttackCheckOutcome {
  const context: CheckContext = {
    kind: 'attack',
    stat: plan.toHitStat,
    skillId: plan.skill.id,
  };
  return resolveAttackCheck(
    roller,
    {
      skillId: plan.skill.id,
      skillName: plan.skill.name,
      toHitStat: plan.toHitStat,
      toHitStatAbbr: STAT_LABELS[plan.toHitStat].abbr,
      toHitStatMod: plan.toHitMod,
      rank: plan.rank,
      // The player's choice plus every attack-scoped Effect source.
      mode: effective.rollMode(mode, context),
      extraComponents: [...effective.checkComponentsFor(context), ...extraComponents],
    },
    targetEvade,
  );
}

/** Catalogue-free, like `planAttack`: no Buffs, no Debuffs, no worn gear. */
export function attackAgainst(
  roller: Roller,
  character: Character,
  plan: AttackPlan,
  mode: RollMode,
  targetEvade: number | null,
): AttackCheckOutcome {
  return attackAgainstFor(roller, buildEffectiveCharacter(character), plan, mode, targetEvade);
}

/** Step two: how much damage? Called only once the attack is known to land. */
export function damageForEffective(
  roller: Roller,
  effective: EffectiveCharacter,
  plan: AttackPlan,
  outcome: AttackCheckOutcome,
  registry: ContentRegistry,
  aimingRank = 0,
): DamageRollResult {
  const damageStat = plan.profile.baseDamage.stat;
  const damage = rollDamage(roller, {
    baseDice: plan.profile.baseDamage.dice,
    rank5Dice: plan.profile.rank5.addedDamageDice,
    rank: plan.rank,
    damageStat,
    damageStatAbbr: damageStat ? STAT_LABELS[damageStat].abbr : null,
    // The damage Stat Mod is the EFFECTIVE one, so a +Str Buff reaches damage.
    damageStatMod: damageStat ? effective.statMod(damageStat) : null,
    damageType: plan.profile.baseDamage.type,
    degree: outcome.resolution ? outcome.resolution.degree : null,
    floor: effective.character.floor,
    rankDamageDieTable: registry.attackContent.rankDamageDiceTable,
  });
  if (aimingRank < 1) return damage;
  // Core p.184: initial and Rank-5 dice are bonus damage; Rank-10/15 dice are base.
  const bonusCount = 1 + (aimingRank >= 5 ? 1 : 0);
  const baseCount = (aimingRank >= 10 ? 1 : 0) + (aimingRank >= 15 ? 1 : 0);
  const count = bonusCount + baseCount * (damage.criticalHit ? 2 : 1);
  const faces = Array.from({ length: count }, () => roller.roll(4));
  const value = faces.reduce((sum, face) => sum + face, 0);
  return { ...damage, total: damage.total + value, components: [...damage.components, { label: `Aiming ${count}d4${damage.criticalHit && baseCount ? ' (base dice doubled)' : ''}`, value, faces }] };
}

/** Catalogue-free, like `planAttack`: no Buffs, no Debuffs, no worn gear. */
export function damageFor(
  roller: Roller,
  character: Character,
  plan: AttackPlan,
  outcome: AttackCheckOutcome,
  registry: ContentRegistry,
): DamageRollResult {
  return damageForEffective(roller, buildEffectiveCharacter(character), plan, outcome, registry);
}

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}

/**
 * RULE DCC-MANA-002 - casting a Spell spends its Mana cost (C&C p.15).
 *
 * Called when the attack is actually CAST, never when a Spell is merely
 * selected or previewed. The cost is spent whether the attack hits or misses,
 * because the Spell was cast either way.
 */
export type CastCostOutcome =
  | { ok: true; character: Character; manaSpent: number }
  | { ok: false; reason: string };

export function payAttackCost(character: Character, plan: AttackPlan): CastCostOutcome {
  if (plan.manaCost === null) return { ok: true, character, manaSpent: 0 };

  const spend = spendCharacterMana(character, plan.manaCost);
  if (!spend.ok) {
    return {
      ok: false,
      reason: `${plan.skill.name} costs ${plan.manaCost} Mana. ${spend.reason}`,
    };
  }
  return { ok: true, character: spend.character, manaSpent: plan.manaCost };
}

/**
 * RULE DCC-ENC-002 - an attack whose profile carries a cooldown cannot be used
 * again until that cooldown ends (Rules p.17; C&C p.22).
 *
 * Checked against the current encounter. Outside an encounter there is no round
 * or scene to measure against, so nothing is enforced and the GM adjudicates.
 */
export function attackCooldownState(
  plan: AttackPlan,
  runtime: RuntimeState | null,
): { blocked: boolean; reason: string | null } {
  if (!plan.profile.cooldown || !runtime) return { blocked: false, reason: null };
  if (!isOnCooldown(runtime, plan.skill.id)) return { blocked: false, reason: null };
  return {
    blocked: true,
    reason: `${plan.skill.name} is on cooldown (${plan.profile.cooldown}).`,
  };
}

/** Maps a printed cooldown label onto a scope this application can measure. */
export function cooldownScopeFor(label: string): CooldownScope | null {
  const text = label.toLowerCase();
  if (text.includes('per round')) return 'round';
  if (text.includes('per scene')) return 'scene';
  if (text.includes('hour')) return 'hours';
  if (text.includes('per day')) return 'day';
  if (text.includes('floor')) return 'floor';
  return null;
}

/** Starts the attack's cooldown, if it has one and we are in an encounter. */
export function startAttackCooldown(plan: AttackPlan, runtime: RuntimeState): RuntimeState {
  const label = plan.profile.cooldown;
  if (!label) return runtime;
  const scope = cooldownScopeFor(label);
  if (!scope) return runtime;

  const amount = /(\d+)\s*hour/.exec(label.toLowerCase());
  return startCooldown(runtime, {
    sourceId: plan.skill.id,
    scope,
    ...(amount ? { amount: Number(amount[1]) } : {}),
  });
}
